import { ContactListItem } from 'types/contacts';
import { CountDto } from 'types/count';
import {
    MarketingProfileCreateUpdateRequest,
    MarketingProfileDetails,
    MarketingProfileFilterCriteria,
    MarketingProfileListItem,
} from 'types/marketingProfiles';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { objectToQueryParams } from 'utils/linkUtil';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

type PreviewContactsArgs = PaginationProps & { criteria: MarketingProfileFilterCriteria };
type PreviewContactsCountArgs = Pick<PaginationProps, 'filters'> & { criteria: MarketingProfileFilterCriteria };

export const APIMarketingProfilesSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['MarketingProfiles', 'MarketingProfile', 'MarketingProfilesCount'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getMarketingProfilesWithFilters: accumulatedQueryWithFilters<MarketingProfileListItem>(
                builder,
                '/v1/marketing-profiles',
                {
                    providesTags: ['MarketingProfiles'],
                    sort: ['lastModifiedAt,desc'],
                    secondarySort: ['createdAt,desc'],
                    pageSize: 50,
                }
            ),
            previewMarketingProfileContacts: builder.query<QueryResponse<ContactListItem>, PreviewContactsArgs>({
                query: ({ criteria, filters = '', sort = '', page = 0, pageSize = 50 }) => ({
                    url: `/v1/marketing-profiles/contacts${objectToQueryParams({
                        filters,
                        sort,
                        page,
                        size: pageSize,
                    })}`,
                    method: 'POST',
                    body: criteria,
                }),
                merge: (currentCache, newItems) => {
                    if (newItems.first) return newItems;
                    return { ...newItems, content: [...currentCache.content, ...newItems.content] };
                },
                serializeQueryArgs: ({ endpointName, queryArgs: { page, ...rest } }) =>
                    `${endpointName}(${JSON.stringify(rest)})`,
                forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
            }),
            previewMarketingProfileContactsCount: builder.query<CountDto, PreviewContactsCountArgs>({
                query: ({ criteria, filters }) => ({
                    url: `/v1/marketing-profiles/contacts/count${objectToQueryParams({
                        filters: filters ?? '',
                    })}`,
                    method: 'POST',
                    body: criteria,
                }),
                keepUnusedDataFor: 5 * 60,
            }),
            getMarketingProfileById: builder.query<MarketingProfileDetails, number>({
                query: (id) => `/v1/marketing-profiles/${id}`,
                providesTags: (_result, _error, id) => [{ type: 'MarketingProfile', id }],
            }),
            createMarketingProfile: builder.mutation<MarketingProfileDetails, MarketingProfileCreateUpdateRequest>({
                query: (body) => ({
                    url: '/v1/marketing-profiles',
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['MarketingProfiles'],
            }),
            updateMarketingProfile: builder.mutation<
                MarketingProfileDetails,
                { id: number; body: MarketingProfileCreateUpdateRequest }
            >({
                query: ({ id, body }) => ({
                    url: `/v1/marketing-profiles/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => ['MarketingProfiles', { type: 'MarketingProfile', id }],
            }),
            deleteMarketingProfile: builder.mutation<void, number>({
                query: (id) => ({
                    url: `/v1/marketing-profiles/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_result, _error, id) => ['MarketingProfiles', { type: 'MarketingProfile', id }],
            }),
            getMarketingProfilesCount: injectCountEndPoint(builder, '/v1/marketing-profiles', 'MarketingProfilesCount'),
            triggerMarketingProfileSync: builder.mutation<{ executionId: number }, number>({
                query: (id) => ({
                    url: `/v1/marketing-profiles/${id}/sync`,
                    method: 'POST',
                }),
            }),
            getMarketingProfileSyncOutcome: builder.query<
                { status: 'PENDING' | 'PROCESSED' | 'FAILED' | 'SKIPPED'; errorMessage: string | null },
                { profileId: number; executionId: number }
            >({
                query: ({ profileId, executionId }) => `/v1/marketing-profiles/${profileId}/sync/${executionId}`,
            }),
        }),
    });

export const {
    useGetMarketingProfilesWithFiltersQuery,
    useLazyGetMarketingProfilesWithFiltersQuery,
    useLazyGetMarketingProfileByIdQuery,
    useGetMarketingProfileByIdQuery,
    useCreateMarketingProfileMutation,
    useUpdateMarketingProfileMutation,
    useDeleteMarketingProfileMutation,
    useLazyGetMarketingProfilesCountQuery,
    useLazyPreviewMarketingProfileContactsQuery,
    useLazyPreviewMarketingProfileContactsCountQuery,
    useTriggerMarketingProfileSyncMutation,
    useGetMarketingProfileSyncOutcomeQuery,
} = APIMarketingProfilesSlice;
